import { t } from '@if/core/i18n'
import { useMenu } from '@if/mui'
import AddIcon from '@mui/icons-material/Add'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove'
import EditNoteIcon from '@mui/icons-material/EditNote'
import RemoveDoneIcon from '@mui/icons-material/RemoveDone'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { Box, IconButton, Menu, MenuItem, Tooltip } from '@mui/material'
import { ECatchUpLatePaymentMethod } from '@src/types/constants'
import { memo, useCallback } from 'react'
import { EEditPaymentsSubAction } from './EditPaymentsDialog'

interface EditPaymentsSubActionMenuProps {
  setSubAction: (action: EEditPaymentsSubAction) => void
  onSMSReminderBatchChange: (checked: boolean) => void
  canSpreadOverdueAmount: boolean
  setInCatchUpMode: (inCatchUp: boolean) => void
  setInIncreasingPayments: (inIncreasing: boolean) => void
  hasAmountNotReplaced?: boolean
}

const EditPaymentsSubActionMenu = ({
  setSubAction,
  onSMSReminderBatchChange,
  canSpreadOverdueAmount,
  setInCatchUpMode,
  hasAmountNotReplaced,
  setInIncreasingPayments,
}: EditPaymentsSubActionMenuProps) => {
  const [buttonMagicProps, menuMagicProps, handleMagicMenuClose] = useMenu()

  const handleSubActionOpen = useCallback(
    (action: EEditPaymentsSubAction) => () => {
      setSubAction(action)
    },
    [setSubAction],
  )
  const handleMagicMenuMethodSelect = (method: ECatchUpLatePaymentMethod) => {
    handleMagicMenuClose()

    switch (method) {
      case ECatchUpLatePaymentMethod.CatchUpOnSinglePayment:
        setSubAction(EEditPaymentsSubAction.CatchUpAmountOfOverdueForDate)
        break
      case ECatchUpLatePaymentMethod.CatchUpOnRemainingPayments: {
        if (setInCatchUpMode) setInCatchUpMode(true)
        setSubAction(EEditPaymentsSubAction.BatchUpdatePayments)
        break
      }
      case ECatchUpLatePaymentMethod.CatchUpOnNextPayments:
        setSubAction(EEditPaymentsSubAction.SpreadLateAmount)
        break
      default:
        break
    }
  }

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={t('loan.payment.add')}>
        <IconButton onClick={handleSubActionOpen(EEditPaymentsSubAction.AddPayment)} color="primary">
          <AddIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('loan.payment.moveSelectedPayments.title')}>
        <IconButton onClick={handleSubActionOpen(EEditPaymentsSubAction.MovePayments)}>
          <DriveFileMoveIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('loan.payment.increase')}>
        <IconButton
          onClick={() => {
            setInIncreasingPayments(true)
            setSubAction(EEditPaymentsSubAction.BatchUpdatePayments)
          }}
        >
          <TrendingUpIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('loan.payment.reduce')}>
        <IconButton
          onClick={() => {
            setInIncreasingPayments(false)
            setSubAction(EEditPaymentsSubAction.BatchUpdatePayments)
          }}
        >
          <TrendingDownIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('loan.payment.addRecurringPayments.title')}>
        <IconButton onClick={handleSubActionOpen(EEditPaymentsSubAction.AddRecurringPayments)}>
          <CurrencyExchangeIcon />
        </IconButton>
      </Tooltip>
      <IconButton disabled={!hasAmountNotReplaced} {...buttonMagicProps}>
        <AutoFixHighIcon />
      </IconButton>
      <Menu {...menuMagicProps}>
        <MenuItem onClick={() => handleMagicMenuMethodSelect(ECatchUpLatePaymentMethod.CatchUpOnSinglePayment)}>
          {t(`enum.ECatchUpLatePaymentMethod.catchUpOnSinglePayment`)}
        </MenuItem>
        <MenuItem onClick={() => handleMagicMenuMethodSelect(ECatchUpLatePaymentMethod.CatchUpOnRemainingPayments)}>
          {t(`enum.ECatchUpLatePaymentMethod.catchUpOnRemainingPayments`)}
        </MenuItem>
        <MenuItem
          disabled={!canSpreadOverdueAmount}
          onClick={() => handleMagicMenuMethodSelect(ECatchUpLatePaymentMethod.CatchUpOnNextPayments)}
        >
          {t(`enum.ECatchUpLatePaymentMethod.catchUpOnNextPayments`)}
        </MenuItem>
      </Menu>
      <Tooltip title={t('loan.payment.options.addNote')}>
        <IconButton onClick={handleSubActionOpen(EEditPaymentsSubAction.AddNoteToAllPayments)}>
          <EditNoteIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('loan.payment.editPayments.checkAllSMS')}>
        <IconButton onClick={() => onSMSReminderBatchChange(true)}>
          <DoneAllIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('loan.payment.editPayments.uncheckAllSMS')}>
        <IconButton onClick={() => onSMSReminderBatchChange(false)}>
          <RemoveDoneIcon />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

export default memo(EditPaymentsSubActionMenu)
